import React from 'react';
import Configuration from '../../config/Configuration';
import _ from 'lodash';
import { User } from 'oidc-client/src/User';
import { UserManagerEvents } from 'oidc-client/src/UserManagerEvents';
import { WebStorageStateStore } from 'oidc-client/src/WebStorageStateStore';
import { UserManager } from 'oidc-client/src/UserManager';
import { Log } from 'oidc-client/src/Log';
import { SigninState } from 'oidc-client/src/SigninState';
import { RedirectNavigator } from 'oidc-client/src/RedirectNavigator';
// @ts-ignore
import Properties from 'properties';

import {
  AuthProvider,
  Dispatcher,
  Position
} from '../../../index';

import { Service } from '../../services/Service';
import { APP_SETTINGS } from './AuthSettings';
import { IdentitySettings } from '../interfaces/AuthInterface';
import LocalCache from '../../../utility/LocalCache';

export class AuthService extends Service {
  private static _instance: AuthService;

  /**
   * User manager, this is OIDC UserManager instance
   * @type {UserManager}
   * @private
   */
  private _userManager: UserManager;

  /**
   * Latest logged in user info
   * @private
   */
  private _latestUser: User;

  /**
   * User manager local sign-in state
   * It used to create localStorage state object for local users
   * @type {SigninState}
   * @private
   */
  private _localState: SigninState;

  /**
   * User messages to compare against messages from TPAuth
   * If 'login_required' message is matched then 3rd party cookies might be blocked
   * @private
   * @static
   */
  private static _userMessages: string[] = ['login_required', 'interaction_required', 'account_selection_required', 'consent_required', 'Frame window timed out'];

  /**
   * User state messages to compare against messages from TPAuth
   * If these messages are matched then 3rd party cookies might be blocked
   * @private
   * @static
   */
  private static _stateMessages: string[] = ['No matching state found in storage', 'No state in response'];

  /**
   * Clock messages to compare against messages from TPAuth
   * These are used to check whether user/client clock is in offset
   * @private
   * @static
   */
  private static _clockMessages: string[] = ['iat is in the future', 'exp is in the past', 'nbf is in the future'];

  /**
   * If service is _running this flag will be true
   * @type {boolean}
   * @private
   */
  private _running: boolean = false;

  /**
   * _signingOut After user started sign-out procedure this flag will be set to true
   * and will be used to prevent any other request as they will be unnecessary while initial sign-out action is finished
   * @type {boolean}
   * @private
   */
  private _signingOut: boolean = false;

  /**
   * If user setup valid, eg is browser ready for login
   * @type {boolean}
   * @private
   */
  private _isUserSetupValid: boolean = true;

  /**
   * If user is signed in then this flag will be set to true
   * @type {boolean}
   * @private
   */
  private _signedOut: boolean = false;

  /**
   * List of all events activity tracker should be listening for.
   * @type {string[]} _trackingEvents
   * @private
   */
  private _trackingEvents: string[] = ['click', 'keydown', 'mousemove', 'mousewheel'];

  /**
   * Default OAuth2 implicit flow settings
   * @type {IdentitySettings}
   * @private
   * @readonly
   */
  private _settings: IdentitySettings = {

    /**
     * Application base uri eg. https://pd.transperfect.com/
     * @type {string}
     */
    application_uri: '',

    /**
     * Application context path eg. /PD6
     * Defaults to /
     */
    contextPath: '/',

    /**
     * Landing page uri
     * This is the page application will go to after user is signed in
     * @type {string}
     */
    landing_uri: '',

    /**
     * Login uri
     * Login page URI eg /login
     * @type {string}
     */
    login_uri: '',

    /**
     * Redirect uri
     * This is the page TPAuth will be redirected after user id signed in. Usually this is some callback page
     * eg. /signin/callback
     * @type {string}
     */
    redirect_uri: ''
  };

  private redirectNavigator: RedirectNavigator = new RedirectNavigator();

  /**
   * When sign-in action has stared this flag is set to true to prevent multiple sign-in actions as well as any other action that can interfere
   * with sign-in
   * @private
   * @static
   */
  private static _signInPending;

  /**
   * When silent sign-in action has started this flag is set to true to prevent multiple silent actions as well as any other action that can interfere
   * with silent sign-in
   * @private
   * @static
   */
  private static _refreshSignInPending;

  /**
   * If silent sign-in action timeouts then this flag is set to true and it will wait for one more silent request
   * before request is stopped with error message - request timed-out
   * @private
   * @static
   */
  private static _refreshSignInTimedOut;

  /**
   * When user expired status is tested then this flag is set to true to prevent multiple expiring actions as well as any other action that can interfere
   * with testing expired status
   * @private
   * @static
   */
  private static _refreshExpiringPending;

  /**
   * If silent sign-in action timeouts then this flag is set to true and it will wait for one more silent request
   * before request is stopped with error message - request timed-out
   * @private
   * @static
   */
  private static _refreshExpiringTimer;

  /**
   * Prevents multiple callbacks, if there is an active Promise it will return that promise
   * @private
   * @static
   */
  private static _redirectCallbackPending;

  /**
   * Debug mode flag. If this is set to `true` all events from UserManager will be logged into console
   * and/or server log if log end-point URI is available
   * @type {boolean}
   * @private
   * @static
   */
  static debugMode: boolean = true;

  /**
   * Storybook helper flag
   * This should be used only when using Dispatcher as story
   * @type {boolean}
   */
  public useStoryHelper: boolean = false;

  /**
   * Returns sign-out url from setting file
   */
  getSignOutUrl = () => Configuration.get().APP_SETTINGS?.post_logout_redirect_uri

  /**
   * Returns proxy sign-out url from setting file
   */
  getProxySignOutUrl = () => Configuration.get().APP_SETTINGS?.proxy_logout_redirect_uri

  static get Instance() {
    return this._instance || (this._instance = new this());
  }

  /**
   * Starts auth service
   * Default props are for development server localhost:3000
   */
  runService = () => {
    const { AUTH: AUTH_SETTINGS, BASE_URL, BASE_ROUTE_NAME } = Configuration.get();
    if (!this._running) {
      AuthService.enableDebug(!!process.env.REACT_APP_DEBUG);
      let baseURL = BASE_ROUTE_NAME.length > 1 ? `${BASE_URL}${BASE_ROUTE_NAME}` : BASE_URL;

      _.assign(this._settings, AUTH_SETTINGS, APP_SETTINGS, {
        post_logout_redirect_uri: `${baseURL}${APP_SETTINGS.post_logout_redirect_uri}`,
        redirect_uri: `${baseURL}${APP_SETTINGS.redirect_uri}`,
        silent_redirect_uri: `${baseURL}${APP_SETTINGS.silent_redirect_uri}`,
        silentRequestTimeout: 30000
      });

      const filterableUrl = window.location.href;
      if (filterableUrl.includes('submissionList')) {
        localStorage.setItem('filter', filterableUrl);
      }

      this._userManager = new UserManager({
        ...this._settings,
        userStore: new WebStorageStateStore({ store: localStorage })
      } as any);

      this.getUser().then((user: User) => this.onUserLoaded(user)).catch(e => e);

      // Connect user events, eg. user loaded or user signed out
      this.registerUserEvents();

      this._running = true;
    }
  };

  /**
   * Returns the running status of the AuthService
   * @returns {boolean}
   */
  isRunning(): boolean {
    return this._running;
  }

  /**
   * Clears localStorage from all oidc\service state logs
   */
  clearLocalState = () => {
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key) && key.match('(service|oidc).') && !key.match('(service|oidc).user')) {
        localStorage.removeItem(key);
      }
    }
  };

  /**
   * Validates current location pathname against url
   * @param {string} path
   * @return {boolean}
   */
  matchPath = (path: string): boolean => new RegExp(path.replace(/^\//, '') + '$', 'i').test(window.location.pathname);

  /**
   * Checks local user session. If local session user is not the same as sessionStore user then app will have to be
   * reloaded to get latest user info
   * @param {User} user
   */
  checkLocalSessions = (user) => {
    if (!this.matchPath(APP_SETTINGS.login_uri) && !AuthProvider.isAuthPath() && !this._signingOut && !this._signedOut && user && this._latestUser) {
      const localJWT = this._userManager._joseUtil.parseJwt(this._latestUser.access_token);

      const userJWT = this._userManager._joseUtil.parseJwt(user.access_token);

      if (localJWT.payload.sub !== userJWT.payload.sub) {
        delete this._latestUser;
        this.dispatch('userChangedWarning', Position.end);
      }
    }
  };

  /**
   * getAccessToken Returns latest access_token value
   * @returns {string}
   */
  getAccessToken() {
    let service = this;
    return this.getUser().then(function (user) {
      if (!user) return;
      if (!user.expired) return user.access_token;

      return service
        .refreshIfExpiring()
        .then(function (user) {
          // if user token is expired lets get another one
          if (!user) return;
          return user.access_token;
        })
        .catch(function (e) {
          AuthService._debugMessage({
            type: 'OAuth2_USER_EXPIRED',
            trace: e.trace(),
            message: e.message,
            error: e.error
          });
          return e;
        });
    }).catch(e => e);
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * getChangePasswordUrl Returns end_session_endpoint with redirect uri query
   * @returns {string}
   */
  getChangePasswordUrl() {
    return `${this._settings.change_password_uri}?redir=${this._settings.redirect_uri}`;
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * getClaims Returns all user claims
   * @returns {Promise|PromiseLike<any>|Promise<any>}
   */
  getClaims() {
    let service = this;

    return this._userManager._tokenClient._metadataService.getUserInfoEndpoint().then(function (userInfo) {
      return service.getAccessToken().then(function (accessToken) {
        return service._userManager._tokenClient._jsonService.getJson(userInfo, accessToken).then(claims => claims).catch(e => e);
      }).catch(e => e);
    }).catch(e => e);
  }

  // noinspection JSUnusedGlobalSymbols
  getClientId(): string {
    return this._settings.client_id;
  }

  /**
   * Returns the landing page where user will end-up after being signed in
   * If local storage contains items used for filtering, landing page will be = absolute path + filter item
   */
  getLandingUri = () => {
    let path = Configuration.get('APP_LANDING_URL');
    !path && (path = APP_SETTINGS.landing_uri);

    let localCache = LocalCache.get();
    let route = localCache.route;

    if (route) {
      path = route;

      this.isAuthenticated().then((authenticated) => {
        // route should be used only once
        authenticated && LocalCache.remove(['route']);
      }).catch(e => e);
    }

    const filter = localCache.filter;
    if (filter) path = `/${filter}`;

    return path;
  };

  /**
   * Returns refresh_token string
   * @returns {Promise<string>}
   */
  getRefreshToken = (): Promise<string> => this.getUser().then(user => user && user.refresh_token).catch(e => e);

  /**
   * Returns current user info
   * @returns {Promise<User>}
   */
  async getUser(): Promise<User> {
    const { IS_NOT_LOGIN_APP } = Properties;
    if (IS_NOT_LOGIN_APP) {
      return {
        profile: {
          email: 'dispatcher@oauth.com',
          sub: 'dispatcher'
        }
      };
    } else {
      let service = this;
      if (!service._userManager) return new Promise((resolve, reject) => reject(new Error('No user manager')));

      return await service._userManager.getUser().then((user) => {
        !service.useStoryHelper && service.checkLocalSessions(user);
        return user;
      }).catch(e => e);
    }
  }

  /**
   * Loads local user info and registers token listeners
   * @param {User} user
   * @returns {Promise<void>}
   */
  loadLocalUser = (user): Promise<void> => {
    let service = this;
    this._signedOut = false;
    this._latestUser = user;

    return service.isAuthenticated().then((authenticated) => {
      if (authenticated && !this.matchPath(APP_SETTINGS.login_uri)) {
        // token refresh listeners
        service.registerTokenListeners(true);
      }
    }).catch(e => e);
  };

  /**
   * Returns true if user is local
   * @param {any} user
   */
  isLocalUser = user => user && _.get(user, 'refresh_token');

  /**
   * Returns true if global logout is enabled in settings
   */
  isGlobalLogoutEnabled = () => Configuration.get('AUTH_GLOBAL_LOGOUT');

  /**
   * Loads user info and sets token listeners as well as post-message listeners for check session IFrame
   * @param {User} user
   * @returns {Promise<void>}
   */
  loadUser = (user): Promise<void> => {
    let service = this;
    this._signedOut = false;

    this._latestUser = user;

    return service.isAuthenticated().then((authenticated) => {
      if (authenticated && !this.matchPath(APP_SETTINGS.login_uri)) {
        service.registerTokenListeners(true); // token refresh listeners

        window.removeEventListener('message', service.onUserMessage); // remove token iframe messages
        window.addEventListener('message', service.onUserMessage); // add token iframe messages
      }
    }).catch(e => e);
  };

  /**
   * Verifies user authentication
   * @return {Promise<boolean>}
   */
  isAuthenticated = async (): Promise<boolean> => await this.getUser().then(user => user && !user.expired).catch(e => e);

  /**
   * Verifies user authentication async
   * @return {Promise<boolean>}
   */
  isAuthenticatedAsync = () => {
    const { IS_NOT_LOGIN_APP } = Properties;
    if (IS_NOT_LOGIN_APP) {
      return true;
    } else {
      let userStoreKey = this._userManager._userStoreKey;
      let user = LocalCache.getExternal('oidc.' + userStoreKey);
      if (user) {
        const newUser = new User(JSON.parse(user));
        return newUser && !newUser.expired;
      }
      return false;
    }
  }

  /**
   * Verifies user setup
   * @return {Promise<boolean>}
   */
  isUserValid = async (): Promise<boolean> => {
    let service = this;
    if (!service._userManager) return new Promise((resolve, reject) => reject(new Error('No user manager')));
    return await service._userManager.signinSilent().then((user) => {
      service._isUserSetupValid = true;
      return user && !user.expired;
    }).catch(function (e) {
      if (e && e.message === 'login_required' && !service.matchPath(APP_SETTINGS.login_uri)) {
        service._isUserSetupValid = false;

        service.dispatch('systemSetupWarning', Position.start);
        // service.dispatch('stopAll');
      }

      return false;
    });
  }

  /**
   * Message token listener
   * This listener will wait for postMessages from IFrames, silent or session IFrame
   * It is used to track user session in real time
   */
  onUserMessage = (message) => {
    let service = this;
    if (window.location.href.startsWith(message.origin) && this._isUserSetupValid) {
      // noinspection JSIgnoredPromiseFromCall
      this.parseUserEventMessage(message)
        .then((message) => {
          service.dispatch('signedOutWarning');
        })
        .catch(e => this.onErrorMessage(e));
    }
  };

  /**
   * Log error message on any user action
   * @param e
   * @returns e
   */
  onErrorMessage = (e) => {
    if (!e) return;

    AuthService._debugMessage({
      errorType: 'TPAUTH_ERROR_MESSAGE',
      errorMessage: e.error,
      errorDescription: e.message
    });

    return e;
  };

  /**
   * User loaded event listener
   * If user is loaded or renewed all user info should be saved
   * @param {UserManagerEvents.UserLoadedCallback} user
   */
  onUserLoaded = async (user: User) => {
    if (user && user.refresh_token) {
      // only local user has refresh_token

      const jwt = this._userManager._joseUtil.parseJwt(user.access_token);
      _.assign(user, {
        expires_at: jwt.payload.exp
      });

      if (!user.toStorageString) {
        // prepare user data for local store
        user.toStorageString = this._toStorageString.bind(this, user);
      }

      user = new User(user);
      return this.loadLocalUser(user);
    } else {
      return this.loadUser(user);
    }
  };

  /**
   * If user is signed out this event handler will be triggered.
   * Handle warning message and after logout logic
   */
  onUserSignedOut = () => {
    let service = this;
    this._signingOut = false;
    return this.isAuthenticated().then((authenticated) => {
      if (!this._signedOut && authenticated) {
        this.refreshOrSignIn()
          .then(function (user) {
            if (!user) {
              LocalCache.set({
                state: 'login_required'
              });
              service.userSignedOut();
            } else {
              LocalCache.remove(['state']);
            }
          })
          .catch(function () {
            LocalCache.set({
              state: 'login_required'
            });

            service.userSignedOut();
          });
      } else {
        service.dispatch('signedOutWarning', Position.end);
      }
    }).catch(e => e);
  };

  /**
   * Parses clock event message and shows user message if user/client clock is in the offset
   * @param {string} message
   */
  parseClockEventMessage = (message) => {
    for (let key in AuthService._clockMessages) {
      if (AuthService._clockMessages.hasOwnProperty(key) && this._isUserSetupValid && message.startsWith(AuthService._clockMessages[key])) {
        this.dispatch('systemClockWarning', Position.end);
        break;
      }
    }
  };

  /**
   * Parses state event message
   * @param {string} message
   */
  parseStateEventMessage = (message) => {
    // noinspection JSUnusedAssignment
    let isRedirectRequired = true;

    if (AuthService._stateMessages.indexOf(message) !== -1) {
      // if there is no user on TPAuth go back to login
      let stateStorageKey = `service.state.${this._settings.client_id}`;
      if (!localStorage.getItem(stateStorageKey)) {
        localStorage.setItem(stateStorageKey, message);
        this.signIn();
      } else {
        localStorage.removeItem(stateStorageKey);
        localStorage.removeItem('login');

        // <debug>
        isRedirectRequired = !this.useStoryHelper;
        // </debug>

        if (isRedirectRequired) {
          this.dispatch('redirectTo', null, {
            path: APP_SETTINGS.login_uri
          });
        }
      }
    }
  };

  /**
   * Parses user event message and returns a message if user action is required
   * @param {string} message
   * @returns {Promise<string>}
   */
  parseUserEventMessage = async (message) => {
    message = await AuthService.userActionRequired(message);

    return new Promise((resolve, reject) => {
      if (_.isString(message)) {
        AuthService._debugMessage(
          {
            trace: 'message',
            message: message
          },
          'warn'
        );

        resolve(message);
      }
      reject();
    });
  }

  /**
   * refreshOrSignIn Refresh token if it isn't expired yet or sign-in if user expired
   * @returns {Promise<User>}
   */
  async refreshOrSignIn() {
    let service = this;
    if (this._signingOut) return new Promise((resolve, reject) => reject(new Error('signing out'))).catch(e => e); // prevent token refresh if user is signing out
    if (AuthService._refreshSignInPending) return AuthService._refreshSignInPending; // if there is already pending promise return the same promise to prevent multiple requests

    AuthService._refreshSignInPending = ((await service.getRefreshToken()) ? service.refreshToken() : service._userManager.signinSilent())
      .then(function (user) {
        service._latestUser = user;
        return user;
      })
      .catch(function (e) {
        AuthService._debugMessage({
          trace: 'refreshOrSignIn - request failed or didn\'t return user object',
          message: e.message,
          error: e.error
        });

        if (!e) return e;

        service
          .parseUserEventMessage(e.message)
          .then((message) => {
            if (message === 'Frame window timed out' && !AuthService._refreshSignInTimedOut) {
              // if request returned timed out exception lets repeat that request
              LocalCache.set({ state: 'login_required' });
              AuthService._refreshSignInTimedOut = true;
              service.refreshOrSignIn();
              return e;
            }

            if (message) {
              // if there was error message notify application
              (window.top || window).postMessage(message, window.location.origin);
            }
          })
          .catch(e => service.onErrorMessage(e));

        delete AuthService._refreshSignInTimedOut;
        return e;
      })
      .finally(() => {
        delete AuthService._refreshSignInPending;
      });

    return AuthService._refreshSignInPending;
  }

  /**
   * Ask for new access_token if it is about to expire
   * @returns {Promise<User>}
   */
  refreshIfExpiring(): any {
    let service = this;

    // if there is already pending promise return the same promise to prevent multiple requests
    if (AuthService._refreshExpiringPending) return AuthService._refreshExpiringPending;

    AuthService._refreshExpiringPending = service
      .getUser()
      .then(function (user) {
        // clear token validation timer
        AuthService._refreshExpiringTimer && clearTimeout(AuthService._refreshExpiringTimer);

        service._latestUser = user;
        if (!user) return;

        if (!user.expired) {
          let expires_in = user.expires_in;
          if (user.expires_in <= 60) {
            expires_in = 60;
          } else {
            expires_in = expires_in - 60;
          }

          AuthService._refreshExpiringTimer = setTimeout(function () {
            AuthService._debugMessage(
              {
                trace: 'refreshIfExpiring - timer triggered'
              },
              'log'
            );
            service.refreshIfExpiring().catch(e => e);
          }, expires_in * 1000);
        }

        if (user.expires_in < 120 || user.expired) return service.refreshOrSignIn().catch(e => e);
        return user;
      })
      .catch(function (e) {
        AuthService._debugMessage(
          {
            trace: 'refreshIfExpiring - request failed or didn\'t return user object',
            message: e.message,
            error: e.error
          },
          'warn'
        );

        if (!e) return;

        service
          .parseUserEventMessage(e.message)
          .then((message) => {
            LocalCache.set({ state: 'login_required' });

            // clear token validation timer
            AuthService._refreshExpiringTimer && clearTimeout(AuthService._refreshExpiringTimer);

            // if there was error message notify application
            ((window && window.top) || window).postMessage(message, window.location.origin);
          })
          .catch(e => service.onErrorMessage(e));

        return e;
      })
      .finally(() => {
        delete AuthService._refreshExpiringPending;
      });

    return AuthService._refreshExpiringPending;
  }

  /**
   * Returns a new access_token
   * @returns {Promise<User>}
   */
  refreshToken(): Promise<User> {
    return this.getUser().then(user =>
      // noinspection TypeScriptValidateJSTypes
      Dispatcher.Axios.request(
        {
          isAuthRest: true,
          method: 'post',
          url: '/oauth/token'
        },
        {
          params: {
            refresh_token: user.refresh_token,
            grant_type: 'refresh_token'
          }
        },
        'userResponse'
      )
        .then(response => this.storeLocalUser(response.data)
          .then(() => response.data))
        .catch(e => e)
    ).catch(e => e);
  }

  /**
   * Do clean page reload
   * Should be used after user is invalidated/removed to be redirected to login
   */
  reload() {
    window.location.reload();
  }

  /**
   * Removes user with all local logs that were saved
   * @returns {Promise<void>}
   */
  removeUser = (): Promise<void> => {
    let service = this;
    this._signingOut = true;

    localStorage.removeItem(`service.state.${service._settings.client_id}`);

    return service._userManager.clearStaleState()
      .then(() => service._userManager.removeUser()
        .then(() => {
          LocalCache.set({ state: undefined });
          service.clearLocalState();
        })
        .catch(e => e))
      .catch(e => e);
  };

  /**
   * Adds user manager events
   * These events will track user state and either load user or remove user
   */
  registerUserEvents = () => {
    this._userManager.events.addUserLoaded(this.onUserLoaded);
    this._userManager.events.addUserSignedOut(this.onUserSignedOut);
  };

  /**
   * Attach/detach activity events, eg mouse and keyboard usage
   * This will attach/detach tracking events on page document so that events are tracked on global scope.
   * @param {boolean} addEvent
   */
  registerTokenListeners = (addEvent) => {
    let event;
    let index = this._trackingEvents.length;
    let eventMethod = addEvent ? 'addEventListener' : 'removeEventListener';
    while (index--) {
      event = this._trackingEvents[index];
      addEvent && document.removeEventListener(event, this.validateToken); // each event will be removed before it is added to avoid multiple listeners
      document[eventMethod](event, this.validateToken);
    }
  };

  /**
   * Remote message log
   * AuthService will send remote message if there is a remote log uri in the settings
   * @param {string} message
   */
  remoteLog = (message: string) => {
    const { REMOTE_LOG_URL } = Configuration.get();
    if (REMOTE_LOG_URL) {
      this.dispatch(
        'remoteLog',
        {},
        {
          url: REMOTE_LOG_URL,
          params: {
            message
          }
        }
      );
    }
  };

  /**
   * signOut Redirects user to sign-out endpoint and navigates back to original application
   * @returns {Promise}
   */
  signOut() {
    let service = this;
    this._signingOut = true; // used to prevent any other requests

    return Promise.resolve(
      service._userManager.signoutRedirect({
        state: 'logout_required'
      })
    );
  }

  /**
   * SignOut redirect callback fires after user has been logged out
   * All user data should be cleared and user should be redirected to login page
   */
  signedOutRedirectCallback = () => {
    let service = this;
    return this._userManager
      .signoutRedirectCallback()
      .catch((e) => {
        AuthService._debugMessage({
          trace: 'signedOutRedirectCallback - debug error',
          message: e.message,
          error: e.error
        });
      })
      .finally(() => service.finishSignOut());
  };

  /**
   * SignOut redirect callback fires after user has been logged out for proxy users
   * All user data should be cleared and user should be redirected to signOut page
   */
  proxySignedOutRedirectCallback = () => {
    const proxySignOutUrl = this.getProxySignOutUrl();
    this.redirectNavigator.navigate({
      url: proxySignOutUrl
    });
  };

  /**
   * Finish sign-out action and set all flags that user has been signed out
   */
  finishSignOut = () => {
    let service = this;
    service.removeUser()
      .catch(e => e)
      .finally(() => {
        service._signingOut = false;
        service._signedOut = true;
        let path: string = APP_SETTINGS.login_uri;
        const { BASE_ROUTE_NAME: subDirectory } = Configuration.get();
        if (subDirectory.length > 1) {
          path = subDirectory + path;
        }

        // <debug>
        if (!process.env?.STORYBOOK) {
          // </debug>
          location.replace(path);
          // <debug>
        }
        // </debug>
      });
  };

  /**
   * Stores local user info into localStorage
   * @param {User} user
   * @returns Promise<void>
   */
  storeLocalUser = (user): Promise<void> => {
    let service = this;

    const jwt = this._userManager._joseUtil.parseJwt(user.access_token);
    _.assign(user, {
      expires_at: jwt.payload.exp
    });

    if (!user.toStorageString) {
      // prepare user data for local store
      user.toStorageString = this._toStorageString.bind(this, user);
    }

    user = new User(user);

    this._localState && this._userManager._stateStore.remove(this._localState.id);
    this._localState = new SigninState({
      nonce: false,
      client_id: 'gl-application'
    });

    return service._userManager._stateStore.set(service._localState.id, service._localState.toStorageString()).then(() => service._userManager.storeUser(user).then(() => {
      this._userManager._events.load(user, true).catch(e => e);
      return user;
    })).catch(e => e);
  };

  /**
   * Runs sign in procedure
   */
  signIn = (state: any = {}, login_hint: string = undefined) => {
    let service = this;
    service._isUserSetupValid = true;

    LocalCache.set({
      state: undefined,
      local_state: undefined
    });

    if (this._signingOut) return new Promise((resolve, reject) => reject(new Error('signing out'))).catch(e => e); // prevent token refresh if user is signing out
    if (AuthService._signInPending) return AuthService._signInPending; // if there is already pending promise return the same promise to prevent multiple requests

    AuthService._signInPending = service._userManager.clearStaleState().then(function () {
      service.clearLocalState();

      service._userManager
        .signinRedirect({
          state,
          login_hint
        })
        .catch(e => e)
        .finally(function () {
          // do a full page redirect to TPAuth authorization end-point to login
          delete AuthService._signInPending;
        });
    }).catch(e => e);

    return AuthService._signInPending;
  };

  /**
   * After signIn event handler
   * After user has signed in user should be redirected to application
   * @param {string} uri
   */
  signInRedirectCallback = (uri: string = undefined) => {
    let service = this;
    if (AuthService._redirectCallbackPending) return AuthService._redirectCallbackPending;

    AuthService._redirectCallbackPending = service._userManager.clearStaleState().then(function () {
      return service._userManager
        .signinRedirectCallback(_.isString(uri) ? uri : undefined)
        .then(function (user) {
          user && user.state && LocalCache.setItem('state', user.state);
          localStorage.removeItem(`service.state.${service._settings.client_id}`);
          service.onUserLoaded(user)
            .then(() => user)
            .catch(e => e);

          return user;
        })
        .catch(function (e) {
          AuthService._debugMessage({
            trace: 'signInRedirectCallback - sign-in request failed or didn\'t return user object',
            message: e.message,
            error: e.error
          });
          service.isUserValid()
            .catch(e => e)
            .finally(() => {
              service.parseStateEventMessage(e.message);
              service.parseClockEventMessage(e.message);
            });

          return e;
        })
        .finally(() => {
          delete AuthService._redirectCallbackPending;
        });
    }).catch(e => e);

    return AuthService._redirectCallbackPending;
  };

  /**
   * Runs silent signIn request
   */
  signInSilentRequest = () => this._userManager.signinSilent().catch(e => e);

  /**
   * Silent signIn callback
   * @private
   */
  signInSilentCallback = () => this._userManager.signinSilentCallback().catch(e => e);

  /**
   * Send email with subject and content
   * @param {string} email
   * @param {string} subject
   * @param {string} content
   */
  sendEmail(email: string, subject: string, content: string) {
    subject = encodeURIComponent(subject);
    content = encodeURIComponent(content);
    window.open(`mailto: ${email}?Subject=${subject}&body=${content}`, '_blank');
  }

  /**
   * userActionRequired After TPAuth returns any message from the messages list will trigger user action
   * @returns {Promise}
   * @static
   */
  static userActionRequired(e) {
    let message = e;
    if (message && typeof message !== 'string') {
      message = e.error || e.data || e.message;
    }

    return new Promise((resolve, reject) => {
      if (this._userMessages.includes(message)) {
        resolve(message);
      }

      reject(new Error('User action not required.'));
    });
  }

  /**
   * User signed out
   * Clear all user metadata
   */
  userSignedOut = () => {
    let service = this;
    this.finishSignOut();
    service._userManager.removeUser().then(() => {
      service._userManager.clearStaleState().then(() => {
        service.clearLocalState();
        service.dispatch('signedOutWarning');
      }).catch(e => e);
    }).catch(e => e);
  };

  /**
   * Refresh token listener
   * This listeners will test user expired state on any user interaction with browser, eg. mouse click, mouse move, keyboard events
   */
  validateToken = () => {
    if (!this.matchPath(APP_SETTINGS.login_uri)) {
      this.refreshIfExpiring()
        .then(function (user) {
          if (!user) {
            AuthService._debugMessage({
              errorType: 'TPAUTH_USER_TOKEN_REFRESH',
              errorMessage: 'No user found',
              errorDescription: 'There was no user returned from TPAuth'
            });
          }
        })
        .catch(function (e) {
          AuthService._debugMessage({
            errorType: 'TPAUTH_USER_TOKEN_REFRESH',
            errorMessage: e.error,
            errorDescription: e.message
          });
        });
    }
  };

  /**
   * Sets debug mode ON or OFF
   * @param {boolean} enabled If called without param `enabled` will default to true
   * @static
   */
  static enableDebug = (enabled: boolean = true) => {
    AuthService.debugMode = enabled;

    Log.logger = console;
    Log.level = enabled ? Log.DEBUG : Log.NONE;

    const error = Log.error;
    Log.error = function (message, log) {
      AuthService._debugMessage({
        messageType: 'TPAUTH_ERROR',
        message: message
      });
      error(arguments);
    };

    const warn = Log.warn;
    Log.warn = function (message, log) {
      AuthService._debugMessage(
        {
          messageType: 'TPAUTH_WARNING',
          message: message
        },
        'warn'
      );
      warn(arguments);
    };
  };

  /**
   * Returns user data as JSON string to be written to local storage
   * @param {User} user
   */
  private _toStorageString = (user: User) => JSON.stringify({
    ...user,
    created: Date.now()
  });

  /**
   * Print a debug message
   * @param {Object} message
   * @param {string} type
   */
  private static _debugMessage(message, type = 'error') {
    if (AuthService.debugMode) {
      console[type]('TPAuth UI Debug: ', message);
    }
  }
}

const AuthServiceInstance = AuthService.Instance;
export default AuthServiceInstance;
